import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isSpliceSearchResult, type SpliceSearchResult } from "@/shared/splice-search.api";
import { isObject, isStringArray } from "@/shared/utils/typeGuards";

/**
 * Searches the samples the signed-in user has licensed on Splice: the
 * plugin's "Your library" filter, through Splice's own GraphQL gateway rather
 * than Atlantis. Results come back in the same shape as a catalog search so
 * the dock renders them with the same rows.
 */
export const kLibrarySearchRoute = "/library-search";

/** The gateway allows up to 50 a page; the dock asks for fewer at a time. */
export const kMaxLibrarySearchCount = 50;

export type LibrarySearchCategory = "loop" | "oneshot";
export type LibrarySearchSort = "relevance" | "popularity" | "recency" | "random";

export interface LibrarySearchRequestBody {
    readonly query: string;
    readonly count: number;
    readonly page: number;
    readonly assetCategory?: LibrarySearchCategory;
    /**
     * Tag labels. The gateway filters by tag id, which the dock does not
     * have, so these join the query text instead.
     */
    readonly tags?: readonly string[];
    /** Lower-case root with a sharp where needed: `c#`. */
    readonly key?: string;
    readonly chordType?: "major" | "minor";
    readonly bpmMin?: number;
    readonly bpmMax?: number;
    readonly sort?: LibrarySearchSort;
}

const kCategories: readonly string[] = ["loop", "oneshot"];
const kSorts: readonly string[] = ["relevance", "popularity", "recency", "random"];

export const isLibrarySearchRequestBody = (value: unknown): value is LibrarySearchRequestBody =>
    isObject(value) &&
    typeof value.query === "string" &&
    typeof value.count === "number" &&
    typeof value.page === "number" &&
    (value.assetCategory === undefined ||
        (typeof value.assetCategory === "string" && kCategories.includes(value.assetCategory))) &&
    (value.tags === undefined || isStringArray(value.tags)) &&
    (value.key === undefined || typeof value.key === "string") &&
    (value.chordType === undefined || value.chordType === "major" || value.chordType === "minor") &&
    (value.bpmMin === undefined || typeof value.bpmMin === "number") &&
    (value.bpmMax === undefined || typeof value.bpmMax === "number") &&
    (value.sort === undefined || (typeof value.sort === "string" && kSorts.includes(value.sort)));

export interface LibrarySearchSuccessResponse {
    readonly ok: true;
    readonly results: readonly SpliceSearchResult[];
    /** The tags the returned samples share most, for the dock's tag row. */
    readonly tagSummary: readonly string[];
    readonly hasMore: boolean;
}

export const isLibrarySearchSuccessResponse = (
    value: unknown,
): value is LibrarySearchSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.results) &&
    value.results.every(isSpliceSearchResult) &&
    isStringArray(value.tagSummary) &&
    typeof value.hasMore === "boolean";

export const kLibrarySearchEndpoint: ApiEndpoint<
    LibrarySearchRequestBody,
    LibrarySearchSuccessResponse
> = {
    route: kLibrarySearchRoute,
    label: "Library Search",
    source: "Splice",
    category: "Semantic Intent",
    description: "Searches the samples the signed-in user has licensed on Splice.",
    isRequestBody: isLibrarySearchRequestBody,
    isSuccessResponse: isLibrarySearchSuccessResponse,
};
